/**
 * Hotkeys is a small library to help "multiplex" events efficiently.
 * it takes a keyMap and then it uses it to properly call the same event from handlers
 */
import invariant from 'invariant';
import createHotKeyMatcher from 'is-hotkey';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';
import { KeyboardEvent } from 'react';

import { HotkeysOptions, KeyboardHandler, KeyMap, KeyMapValue } from './types';

type InvertedKeyMapValue = {
  handler: string;
  enabled?: (e: KeyboardEvent<any>) => true;
  key: string;
};
type InvertedKeyMap = Record<string, InvertedKeyMapValue>;

// @ts-expect-error Not 100% but return type may be [KeyMapValue, { handler: string, key: KeyMapValue }][]
const getKeymapValue = (handler: string, keyMapValue: KeyMapValue) => {
  if (isString(keyMapValue)) {
    return [[keyMapValue, { handler, key: keyMapValue }]];
  }
  if (isArray(keyMapValue)) {
    return flatten(keyMapValue.map((hotkey) => getKeymapValue(handler, hotkey)));
  }
  if (isObject(keyMapValue)) {
    const { hotkeys, enabled } = keyMapValue;
    const hotkeysToMap = isString(hotkeys) ? [hotkeys] : hotkeys;
    return hotkeysToMap.map((hotkey) => [hotkey, { handler, key: hotkey, enabled }]);
  }
  return [];
};

const createInvertedKeyMap = (keyMap: KeyMap): InvertedKeyMap => {
  const entries = Object.entries(keyMap).map(([handler, keyMapValue]) =>
    getKeymapValue(handler, keyMapValue),
  );
  // @ts-expect-error
  return mapValues(
    // group shortcuts by `keyEntry` so we can store them efficiently
    groupBy(flatten(entries), ([key]) => key),
    // ignore the key of each entry, since we already grouped by it
    (entry) => entry.map((kv: any) => kv[1]),
  );
};

// this returns such function: (e) => boolean
const isHotkey = (key: string) => {
  if (key === '*') {
    return () => true;
  }

  // byKey is required for matching shift+?
  return createHotKeyMatcher(key, { byKey: key.includes('?') });
};

export const hotkeys = ({ keyMap, handlers }: HotkeysOptions): KeyboardHandler => {
  invariant(isObject(keyMap) && !isEmpty(keyMap), 'You must define at least one keymap');
  invariant(isObject(keyMap) && !isEmpty(handlers), 'You must define at least one handler');

  const invertedKeyMap: InvertedKeyMap = createInvertedKeyMap(keyMap);
  const invertedKeys = Object.keys(invertedKeyMap);

  return (e: KeyboardEvent) => {
    const matchingEntries = invertedKeys
      // TODO(mat) this is correct way of using this function
      // but keyboard event types have slight missmatch
      .filter((key) => isHotkey(key)((e as unknown) as globalThis.KeyboardEvent))
      .map((key) => invertedKeyMap[key]);

    flatten(matchingEntries).forEach((invertedEntry) => {
      const { key, handler, enabled } = invertedEntry;
      if (!enabled || (isFunction(enabled) && !!enabled(e))) {
        const matchingHandler = handlers[handler];
        if (!isFunction(matchingHandler)) {
          return;
        }
        matchingHandler(e, key, handler);
      }
    });
  };
};
